import { useState, useMemo, useEffect, useRef } from 'react';
import {
  COUNTDOWN_INTERVAL,
  LOCKOUT_UPDATE_INTERVAL,
  ERROR_TYPES,
} from './constants';
import { formatErrorMessage } from './utils';

export const useVerificationState = () => {
  const [code, setCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [verificationResponse, setVerificationResponse] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resent, setResent] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [resendTrigger, setResendTrigger] = useState(0);

  return {
    code,
    setCode,
    processing,
    setProcessing,
    error,
    setError,
    showSuccess,
    setShowSuccess,
    verificationResponse,
    setVerificationResponse,
    countdownSeconds,
    setCountdownSeconds,
    isResendDisabled,
    setIsResendDisabled,
    resent,
    setResent,
    lockoutUntil,
    setLockoutUntil,
    lockoutMinutes,
    setLockoutMinutes,
    resendTrigger,
    setResendTrigger,
  };
};

export const useLabels = (siteCopy, props, isSignup) => {
  const verificationCodeLabels =
    siteCopy?.pageCopy?.verificationCodeLabels || {};

  return useMemo(
    () => ({
      title: props.title || verificationCodeLabels.title,
      subtitle: props.subtitle || verificationCodeLabels.subtitle,
      resendText:
        props.resendLabel || verificationCodeLabels.resend || 'Resend code',
      resendSuccessText:
        verificationCodeLabels.resendSuccess || 'Code resent successfully.',
      submitText:
        props.submitLabel || verificationCodeLabels.submit || 'Submit',
      errorText:
        verificationCodeLabels.incorrectCode ||
        'Invalid code. Please try again.',
      successText: isSignup
        ? verificationCodeLabels.signupSuccessTitle
        : props.successTitle || verificationCodeLabels.successTitle,
      successButtonText:
        props.successButtonLabel ||
        verificationCodeLabels.successButtonLabel ||
        'OKAY',
      resendTextWithTime:
        verificationCodeLabels.resendWithTime || 'Resend code in #time',
      errorLabels: verificationCodeLabels.errors || {},
      disclaimer: verificationCodeLabels.disclaimer || '',
    }),
    [verificationCodeLabels, props, isSignup],
  );
};

/**
 * Custom hook for managing countdown timer
 * @param {number} countdownSeconds - Current countdown value
 * @param {Function} setCountdownSeconds - Setter for countdown
 * @param {Function} setIsResendDisabled - Setter for resend disabled state
 * @param {number} remainingSeconds - Initial remaining seconds from API
 * @param {number} resendTrigger - Counter that increments on each resend to trigger timer reset
 */
export const useCountdownTimer = (
  countdownSeconds,
  setCountdownSeconds,
  setIsResendDisabled,
  remainingSeconds,
  resendTrigger,
) => {
  const countdownTimerRef = useRef(null);

  useEffect(() => {
    if (remainingSeconds) {
      setCountdownSeconds(remainingSeconds);
      setIsResendDisabled(true);

      // Clear any existing timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }

      // Start countdown timer
      countdownTimerRef.current = setInterval(() => {
        setCountdownSeconds(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setIsResendDisabled(false);
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            return 0;
          }
          return newValue;
        });
      }, COUNTDOWN_INTERVAL);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [
    remainingSeconds,
    resendTrigger,
    setCountdownSeconds,
    setIsResendDisabled,
  ]);

  return countdownTimerRef;
};

/**
 * Custom hook for managing account lockout countdown
 * @param {number} lockoutUntil - Timestamp when lockout expires
 * @param {Function} setLockoutUntil - Setter for lockout timestamp
 * @param {Function} setLockoutMinutes - Setter for remaining minutes
 * @param {Function} setError - Setter for error message
 * @param {Object} labels - Label translations
 */
export const useLockoutTimer = (
  lockoutUntil,
  setLockoutUntil,
  setLockoutMinutes,
  setError,
  labels,
) => {
  useEffect(() => {
    let timer;
    if (lockoutUntil) {
      const updateLockoutMinutes = () => {
        const now = Date.now();
        const remaining = lockoutUntil - now;

        if (remaining <= 0) {
          // Lockout expired
          setLockoutUntil(null);
          setLockoutMinutes(0);
          setError('');
          return false;
        }

        const minutes = Math.ceil(remaining / (60 * 1000));
        setLockoutMinutes(minutes);

        // Update error message with new countdown
        const newError = formatErrorMessage(
          ERROR_TYPES.ACCOUNT_LOCKED,
          { remainingMinutes: minutes },
          labels,
        );

        setError(newError);
        return true;
      };

      // Update immediately
      if (updateLockoutMinutes()) {
        // Then update every minute
        timer = setInterval(() => {
          if (!updateLockoutMinutes()) {
            clearInterval(timer);
          }
        }, LOCKOUT_UPDATE_INTERVAL);
      }
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [lockoutUntil, labels, setLockoutMinutes, setLockoutUntil, setError]);
};
